import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { tokens } from '../../../theme/tokens';

/**
 * Shared pieces of the dealer product view (handoff
 * `design_handoff_dealer_product_view`). Sizes, spacing and copy follow the
 * handoff; colours are the theme tokens nearest each handoff value.
 */

/** Bundle purple has no theme token; it is the same literal the variants table uses for its bundle badge. */
// eslint-disable-next-line no-restricted-syntax
export const BUNDLE_TONE = { bg: '#f5f3ff', ink: '#6d28d9' } as const;

export type Tone = 'positive' | 'caution' | 'problem' | 'info' | 'neutral';

export const toneColors = (tone: Tone) => tokens.signal[tone];

export function Dot({ color, size = 6, sx }: { color: string; size?: number; sx?: SxProps<Theme> }) {
  return <Box component="span" aria-hidden sx={{ flex: `0 0 ${size}px`, width: size, height: size, borderRadius: '50%', bgcolor: color, ...sx }} />;
}

/** 11px uppercase inline section header with an optional count and right-aligned note. */
export function InlineSectionHeader({ title, count, note, action }: { title: string; count?: React.ReactNode; note?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', pb: '8px', borderBottom: '1px solid', borderColor: tokens.line.default, flexWrap: 'wrap' }}>
      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
        {title}
      </Typography>
      {count != null && count !== '' && (
        <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
      )}
      <Box sx={{ flex: '1 1 auto' }} />
      {note && <Typography component="span" sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>{note}</Typography>}
      {action}
    </Box>
  );
}

/** 12.5px uppercase header used on the Details and Specifications tabs. */
export function DetailSectionHeader({ title, count, note }: { title: string; count?: React.ReactNode; note?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', pb: '8px', borderBottom: '1px solid', borderColor: tokens.line.default }}>
      <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.primary }}>
        {title}
      </Typography>
      {count != null && count !== '' && (
        <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
      )}
      <Box sx={{ flex: '1 1 auto' }} />
      {note && <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{note}</Typography>}
    </Box>
  );
}

/** White card, 1px border, radius 12, with an optional 14px/600 title row. */
export function DealerCard({
  title,
  count,
  note,
  actions,
  borderColor,
  children,
  sx,
}: {
  title?: React.ReactNode;
  count?: React.ReactNode;
  note?: React.ReactNode;
  actions?: React.ReactNode;
  borderColor?: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: tokens.surface.card, border: '1px solid', borderColor: borderColor ?? tokens.line.default, borderRadius: '12px', minWidth: 0, ...sx }}>
      {(title || actions) && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', px: '18px', py: '14px', borderBottom: '1px solid', borderColor: tokens.line.hairline, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: tokens.ink.primary }}>{title}</Typography>
            {count != null && count !== '' && (
              <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            {note && <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{note}</Typography>}
            {actions}
          </Box>
        </Box>
      )}
      {children}
    </Box>
  );
}

/** h20 pill with an optional leading 5px dot. */
export function Pill({ label, tone, dot = true, sx }: { label: React.ReactNode; tone: Tone; dot?: boolean; sx?: SxProps<Theme> }) {
  const c = toneColors(tone);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', height: 20, px: '8px', borderRadius: '6px', bgcolor: c.tint, color: c.ink, fontSize: '11.5px', fontWeight: 600, whiteSpace: 'nowrap', ...sx }}>
      {dot && <Dot color={c.solid} size={5} />}
      {label}
    </Box>
  );
}

export type SourceKind = 'vendor' | 'derived' | 'ai' | 'missing' | 'scrape' | 'manual' | 'lookup';

const SOURCE_BADGE: Record<SourceKind, { label: string; bg: string; border: string; ink: string }> = {
  vendor: { label: 'VENDOR', bg: tokens.signal.info.tint, border: tokens.signal.info.border, ink: tokens.signal.info.ink },
  lookup: { label: 'BARCODE LOOKUP', bg: tokens.signal.info.tint, border: tokens.signal.info.border, ink: tokens.signal.info.ink },
  derived: { label: 'DERIVED', bg: tokens.signal.neutral.tint, border: tokens.line.default, ink: tokens.ink.bodyAlt },
  ai: { label: 'AI', bg: BUNDLE_TONE.bg, border: BUNDLE_TONE.bg, ink: BUNDLE_TONE.ink },
  missing: { label: 'MISSING', bg: tokens.signal.problem.tint, border: tokens.signal.problem.border, ink: tokens.signal.problem.ink },
  scrape: { label: 'SCRAPE', bg: tokens.signal.positive.tint, border: tokens.signal.positive.border, ink: tokens.signal.positive.ink },
  manual: { label: 'MOTZU', bg: tokens.signal.neutral.tint, border: tokens.line.default, ink: tokens.ink.bodyAlt },
};

/** 9.5px/700 source badge. Shown only where the source is known; a value with no recorded source gets none. */
export function SourceBadge({ kind }: { kind: SourceKind }) {
  const b = SOURCE_BADGE[kind];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '5px', py: '1px', borderRadius: '4px', border: '1px solid', borderColor: b.border, bgcolor: b.bg, color: b.ink, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      {b.label}
    </Box>
  );
}

/** Amber warning banner: 6px dot, 12.5px text, optional trailing action. */
export function WarnBanner({ children, action, sx }: { children: React.ReactNode; action?: React.ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', px: '11px', py: '9px', borderRadius: '9px', border: '1px solid', borderColor: tokens.signal.caution.border, bgcolor: tokens.signal.caution.tint, flexWrap: 'wrap', ...sx }}>
      <Dot color={tokens.signal.caution.solid} sx={{ mt: '5px' }} />
      <Typography component="div" sx={{ flex: '1 1 240px', fontSize: '12.5px', lineHeight: 1.5, color: tokens.signal.caution.ink }}>{children}</Typography>
      {action}
    </Box>
  );
}

/** Note box whose tone follows a state (price override note). */
export function ToneNote({ tone, children }: { tone: 'neutral' | 'caution' | 'positive'; children: React.ReactNode }) {
  const c = toneColors(tone);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', px: '11px', py: '9px', borderRadius: '9px', border: '1px solid', borderColor: tone === 'neutral' ? tokens.line.default : c.border, bgcolor: tone === 'neutral' ? tokens.surface.sunken : c.tint }}>
      <Dot color={tone === 'neutral' ? tokens.ink.faint : c.solid} sx={{ mt: '5px' }} />
      <Typography component="div" sx={{ fontSize: '12.5px', lineHeight: 1.5, color: tone === 'neutral' ? tokens.ink.bodyAlt : c.ink }}>{children}</Typography>
    </Box>
  );
}

/** Neutral info box: 1px line, sunken ground, radius 10, 12.5px body. */
export function InfoBox({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={{ px: '14px', py: '10px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', bgcolor: tokens.surface.sunken, fontSize: '12.5px', lineHeight: 1.5, color: tokens.ink.bodyAlt, ...sx }}>
      {children}
    </Box>
  );
}

/** Blue 12.5px/600 text button. */
export function TextLink({ children, onClick, href, disabled, sx }: { children: React.ReactNode; onClick?: () => void; href?: string; disabled?: boolean; sx?: SxProps<Theme> }) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      {...(href ? { href, target: '_blank', rel: 'noopener noreferrer', component: 'a' as const } : {})}
      sx={{ alignSelf: 'flex-start', p: 0, fontSize: '12.5px', fontWeight: 600, color: disabled ? tokens.ink.faint : tokens.accent.main, whiteSpace: 'nowrap', '&:hover': { color: tokens.accent.hover, textDecoration: 'underline' }, ...sx }}
    >
      {children}
    </ButtonBase>
  );
}

/** White outlined button (h38 link buttons, h30–34 secondary). */
export function OutlineButton({
  children,
  onClick,
  href,
  height = 38,
  disabled,
  startIcon,
  endIcon,
  sx,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  height?: number;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      {...(href ? { href, target: '_blank', rel: 'noopener noreferrer', component: 'a' as const } : {})}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '7px', height, px: height >= 38 ? '14px' : '12px',
        border: '1px solid', borderColor: tokens.line.default, borderRadius: height >= 38 ? '10px' : '9px', bgcolor: tokens.surface.card,
        color: tokens.ink.primary, fontSize: height >= 38 ? '13.5px' : '12.5px', fontWeight: 600, whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        '&:hover': { borderColor: tokens.ink.disabled },
        ...sx,
      }}
    >
      {startIcon}
      {children}
      {endIcon}
    </ButtonBase>
  );
}

/** Primary blue button, h34 radius 9. Disabled is the accent at reduced strength. */
export function PrimaryButton({ children, onClick, disabled, sx }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; sx?: SxProps<Theme> }) {
  return (
    <ButtonBase
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 34, px: '14px', border: 'none', borderRadius: '9px',
        bgcolor: tokens.accent.main, color: tokens.ink.inverse, fontSize: '13px', fontWeight: 600,
        opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover': disabled ? undefined : { bgcolor: tokens.accent.hover },
        ...sx,
      }}
    >
      {children}
    </ButtonBase>
  );
}

/** 34×20 switch with a label and help line; disabled at 50% opacity. */
export function ToggleRow({ on, label, help, disabled, onToggle }: { on: boolean; label: string; help: string; disabled?: boolean; onToggle: () => void }) {
  return (
    <ButtonBase
      onClick={disabled ? undefined : onToggle}
      role="switch"
      aria-checked={on}
      aria-disabled={disabled || undefined}
      sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', justifyContent: 'flex-start', p: 0, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer' }}
    >
      <Box component="span" sx={{ flex: '0 0 auto', position: 'relative', width: 34, height: 20, borderRadius: '10px', bgcolor: on ? tokens.accent.main : tokens.ink.disabled, transition: 'background 120ms' }}>
        <Box component="span" sx={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '8px', bgcolor: tokens.surface.card, boxShadow: '0 1px 2px rgba(15,23,42,0.2)', transition: 'left 120ms' }} />
      </Box>
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 500, color: tokens.ink.primary }}>{label}</Typography>
        <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.muted }}>{help}</Typography>
      </Box>
    </ButtonBase>
  );
}

/** 10px padlock, currentColor: a value the dealer sees but never advertises. */
export function LockGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
      <rect x="2" y="5" width="8" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3.9 5V3.7a2.1 2.1 0 014.2 0V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function ExternalGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M5 2h7v7M12 2L5.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 9.5V12H2V4h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function SyncGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M13 8a5 5 0 01-8.6 3.5M3 8a5 5 0 018.6-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 12.5V9.5h3M13 3.5v3h-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function SparkleGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M6 1l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" fill="currentColor" />
    </svg>
  );
}

/** 36/40px thumbnail with the product's image, else a faint camera glyph. */
export function Thumb({ src, size = 36, radius = 7 }: { src?: string | null; size?: number; radius?: number }) {
  return (
    <Box sx={{ width: size, height: size, flex: `0 0 ${size}px`, display: 'grid', placeItems: 'center', borderRadius: `${radius}px`, bgcolor: tokens.surface.sunken, border: '1px solid', borderColor: tokens.line.hairline, overflow: 'hidden' }}>
      {src ? (
        <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <svg width={size / 2} height={size / 2} viewBox="0 0 24 24" fill="none" aria-hidden style={{ opacity: 0.45 }}>
          <rect x="3" y="7" width="18" height="13" rx="2" stroke={tokens.ink.muted} strokeWidth="1.5" />
          <path d="M8 7l1.5-2.5h5L16 7" stroke={tokens.ink.muted} strokeWidth="1.5" strokeLinejoin="round" />
          <circle cx="12" cy="13.5" r="3.5" stroke={tokens.ink.muted} strokeWidth="1.5" />
        </svg>
      )}
    </Box>
  );
}
